/**
 * MapProjectionView — the current projection + globe layout encapsulated as a named view.
 * Owns LAYOUT constants, layout-recalculation logic, and interaction handler lifecycle.
 */
import { calculateOptimalSizes, calculatePositions, applyLayout } from '../core/layout-calculator.js';
import { initMouseHandler } from '../ui/mouse-handler.js';
import { initProjectionHover } from '../ui/country-hover.js';

export const LAYOUT = {
    PROJECTION_TOP_MARGIN: 20,        // Top margin for projection canvas
    GLOBE_MARGIN: 30,                 // Margin between projection and globe
    GLOBE_BOTTOM_PADDING: 40,         // Padding from window bottom for globe
    MIN_PROJECTION_WIDTH: 400,        // Minimum usable projection width
    MIN_GLOBE_SIZE: 150,              // Minimum usable globe size
    PROJECTION_GLOBE_SIZE_RATIO: 1/3  // Globe size as ratio of projection width
};

export class MapProjectionView {
    constructor(app) {
        this.app = app;
        this._cleanupMouseHandler    = null;
        this._cleanupProjectionHover = null;
    }

    activate() {
        const { app } = this;
        document.getElementById('controls').style.display = 'block';
        document.getElementById('sidebar-container').style.display = '';

        // Attach interaction handlers — they are only active while this view is shown
        this._cleanupMouseHandler    = initMouseHandler(app, app.renderer.globe.bounds);
        this._cleanupProjectionHover = initProjectionHover(app);
    }

    deactivate() {
        document.getElementById('sidebar-container').style.display = 'none';

        // Remove interaction handlers so they can't fire while another view is active
        this._cleanupMouseHandler?.();
        this._cleanupProjectionHover?.();
        this._cleanupMouseHandler    = null;
        this._cleanupProjectionHover = null;
    }

    applyLayout() {
        const { app } = this;
        if (!app.renderer || !app.earthModel) return;

        const totalWidth  = window.innerWidth;
        const totalHeight = window.innerHeight;

        // Resize PixiJS canvas to full viewport
        app.renderer.app.renderer.resize(totalWidth, totalHeight);

        const ratio     = app.earthModel.getAspectRatio();
        const sizes     = calculateOptimalSizes(totalWidth, totalHeight, ratio, LAYOUT);
        const positions = calculatePositions(sizes, totalWidth, LAYOUT);
        applyLayout(app, sizes, positions);
    }
}
